import { isRef, type CSSProperties } from 'vue'
import type { FormFieldRef } from './types/field'
import type { IForm } from './types/form'
import { isFieldRef } from './formv2/hooks/useForm/utils'
import type { FunctionalComponent } from 'vue'
import type { UseFormReturn } from './formv2/hooks/useForm'
import { unref } from '@/utils/reactive'
import FormField from './form-field'
import type { JSX } from 'vue/jsx-runtime'

export type UseFormFieldRenderProps = {
  schema: IForm
  handler: UseFormReturn
}

export type UseFormFieldRender = {
  renderField: (key: string, field: FormFieldRef) => JSX.Element | null | undefined
}

export const useFormFieldRender = (props: UseFormFieldRenderProps): UseFormFieldRender => {
  const handleInput = (event: Event, field: FormFieldRef, schema: IForm<any>) => {
    props.handler.handleInput(event, field, schema)
  }

  const handleBlur = (event: Event, field: FormFieldRef, schema: IForm<any>) => {
    props.handler.handleBlur(event, field, schema)
  }

  const handleFocus = (event: FocusEvent, field: FormFieldRef, schema: IForm<any>) => {
    props.handler.handleFocus(event, field, schema)
  }

  const renderField = (key: string, field: FormFieldRef) => {
    if (!field) return <div>{key} is null or undefined</div>
    const fieldDef = (props.schema[key as any] as IForm<any>) || field.value.schema
    if (!fieldDef) return <div>Missing field definition</div>
    if (!isFieldRef(field)) return <div>Invalid field</div>

    let block: FormFieldRef | undefined = undefined
    const ff = field

    if (ff && !isRef(ff) && (ff as any).$ref) {
      block = (ff as any).$ref as FormFieldRef
    }

    let hidden = Boolean(field.value && field.value.hidden)
    if (block && block.value.hidden) {
      hidden = block.value.hidden
    }

    const style: CSSProperties = hidden
      ? {
          display: 'none',
          opacity: '0%'
        }
      : {}

    const wrapCustomComponent = (Comp?: FunctionalComponent<any>) => {
      if (!Comp) return undefined
      return () => (
        <Comp
          key={field.value.name}
          state={props.handler.state.value}
          value={field.value.value}
          fieldRef={field}
          reportChange={props.handler.reportChange}
        />
      )
    }

    return (
      <FormField
        componentExtensions={props.handler.props.componentExtensions}
        style={[style, fieldDef.$wrapperCSS as any]}
        range={field.value.range || fieldDef.$range}
        name={key}
        key={key}
        schema={fieldDef}
        class={fieldDef.$wrapperClass}
        value={field.value.value}
        label={field.value.schema.$label}
        labelPlacement={field.value.schema.$labelPlacement}
        labelClass={field.value.schema.$labelClass}
        inlineLabel={field.value.schema.$inlineLabel}
        subLabel={field.value.schema.$subLabel}
        type={field.value.type || 'text'}
        reportChange={props.handler.reportChange}
        triggerBlur={props.handler.triggerBlur || props.handler.triggerBlur}
        validators={field.value.schema.$validators}
        wrapper={field.value.schema.$wrapper}
        componentType={field.value.schema.$componentType}
        locked={field.value.locked}
        lockedReason={field.value.lockedReason}
        fieldRef={field}
        placeholder={field.value.placeholder || field.value.schema.$placeholder}
        placeholderIcon={field.value.schema.$placeholderIcon}
        placeholderClass={field.value.schema.$placeholderClass}
        display={field.value.display}
        title={field.value.schema.$title}
        variant={field.value.schema.$variant}
        icon={field.value.schema.$icon}
        subtitle={field.value.schema.$subtitle}
        subtitleClass={field.value.schema.$subtitleClass}
        css={field.value.schema.$css}
        options={unref(field.value.options) as any}
        injectedProps={field.value.injectedProps}
        numberTweening={field.value.schema.$numberTweening}
        numberPrecision={field.value.schema.$numberPrecision || field.value.numberPrecision}
        required={field.value.required}
        disabled={field.value.disabled}
        before={wrapCustomComponent(field.value.schema.$before)}
        after={wrapCustomComponent(field.value.schema.$after)}
        grid={field.value.schema.$grid}
        errors={(field.value.errors || []).filter((it) => it.hidden !== true)}
        errorProps={field.value.errorProps}
        tooltip={field.value.schema.$tooltip}
        bubbleTip={field.value.schema.$bubbleTip}
        touched={field.value.touched}
        customComponent={wrapCustomComponent(field.value.customComponent)}
        id={field.value.id}
        isLoading={field.value.isLoading}
        placement={field.value.schema.$placement}
        onInput={(e) => handleInput(e as any, field, (props.schema as any)[key] as any)}
        onBlur={(e) => handleBlur(e as any, field, (props.schema as any)[key] as any)}
        onFocus={(e) => handleFocus(e as any, field, (props.schema as any)[key] as any)}
      />
    )
  }

  return { renderField }
}
